// File: src/pages/BookInfo.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import '../styles/BookInfo.css';
import Sidebar from "../components/Sidebar.js";
import Header from "../components/Header.js";
import bigLeftArrow from '../images/bigLeftArrow.png';
import bigRightArrow from '../images/bigRightArrow.png';
import playIcon from '../images/playIcon.png';
import readlaterIcon from '../images/readlater_icon.png';
import tickReadLaterIcon from '../images/tick_readlater.png';
import loadingSpinner from "../images/loadingSpinner.gif";
import { insertReadLater, deleteReadLater, fetchPageDetails } from "../services/AllServices.js";
import { speakText } from "../components/utils/speechUtils.js";

const BookInfo = () => {
  const [activeItem, setActiveItem] = useState("dashboard");
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortedBooks, setSortedBooks] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const { bookId, genre } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchBookData = () => {
      try {
        setLoading(true);
        if (genre && location.state?.genreBooks[genre]) {
          const booksInGenre = location.state.genreBooks[genre];
          setSortedBooks(booksInGenre);

          const selectedIndex = booksInGenre.findIndex((book) => book._id === bookId);
          setCurrentIndex(selectedIndex !== -1 ? selectedIndex : 0);

          // Check bookmark status from local storage
          const currentBook = booksInGenre[selectedIndex] || booksInGenre[0];
          const storedBookmarks = JSON.parse(localStorage.getItem("bookmarks")) || {};
          setIsBookmarked(!!(currentBook && storedBookmarks[currentBook._id]));
        }
      } catch (error) {
        console.error("Error fetching book data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genre, bookId, location.state]);

  const speakCurrentBook = (book) => {
    if (!book || !book.title) return;
    const authorText = (book.author_list && book.author_list.length) ? ` by ${book.author_list.join(", ")}` : "";
    speakText(`${book.title}${authorText}`);
  };

  // announce when loading finishes or current book changes
  useEffect(() => {
    if (!loading) {
      const book = sortedBooks[currentIndex];
      if (book) {
        // small delay so images/text render before speaking
        setTimeout(() => speakCurrentBook(book), 200);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, currentIndex, sortedBooks]);

  const handlePlayClick = async (event, bookID) => {
    event?.preventDefault?.();
    if (!bookID) {
      console.log('No bookID provided:', bookID);
      return;
    }

    try {
      const response = await fetchPageDetails(bookID);
      if (response) {
        const title = response?.title || "book";
        speakText(`Playing ${title}`);
        navigate("/audiobook-player", { state: { bookData: response } });
      } else {
        console.error("Invalid response structure:", response);
      }
    } catch (error) {
      console.error("Error in handlePlayClick:", error);
    }
  };

  const toggleBookmark = async (bookID) => {
    const currentBook = sortedBooks[currentIndex];
    const storedBookmarks = JSON.parse(localStorage.getItem("bookmarks")) || {};

    if (isBookmarked) {
      try { await deleteReadLater({ bookID }); } catch (e) { console.warn(e); }
      delete storedBookmarks[currentBook._id];
      setIsBookmarked(false);
      speakText(`Removed ${currentBook.title} from Read Later`);
    } else {
      try { await insertReadLater({ bookID }); } catch (e) { console.warn(e); }
      storedBookmarks[currentBook._id] = true;
      setIsBookmarked(true);
      speakText(`Added ${currentBook.title} to Read Later`);
    }

    localStorage.setItem("bookmarks", JSON.stringify(storedBookmarks));
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => {
      const nextIndex = (prevIndex + 1) % sortedBooks.length;
      const nextBook = sortedBooks[nextIndex];
      navigate(`/book-info/${genre}/${nextBook._id}`, { state: location.state });
      // speak about upcoming book
      setTimeout(() => speakCurrentBook(nextBook), 150);
      setIsBookmarked(nextBook?.isBookmarked || false);
      return nextIndex;
    });
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => {
      const prevIndexCalc = (prevIndex - 1 + sortedBooks.length) % sortedBooks.length;
      const prevBook = sortedBooks[prevIndexCalc];
      navigate(`/book-info/${genre}/${prevBook._id}`, { state: location.state });
      setTimeout(() => speakCurrentBook(prevBook), 150);
      setIsBookmarked(prevBook?.isBookmarked || false);
      return prevIndexCalc;
    });
  };

  const currentBook = sortedBooks[currentIndex] || {};

  const handleProfileClick = () => {
    navigate("/settings", { state: { selectedSection: "account" } });
  };

  const resetSearch = () => setSearchQuery("");

  const handleActiveItemChange = (item) => {
    setActiveItem(item);
    if (item === "dashboard") navigate("/dashboard");
    else if (item === "bookmarks") navigate("/bookmarks");
    else if (item === "library") navigate("/library");
    else if (item === "settings") navigate("/settings");
    else if (item === "help") navigate("/help");
    resetSearch();
  };

  const getHeaderVisibility = () => {
    if (activeItem === "dashboard" || activeItem === "") {
      return { showSearch: true, showUserProfile: true, showArrows: true, pageName: "Dashboard" };
    } else if (activeItem === "bookmarks") {
      return { showSearch: true, showUserProfile: true, showArrows: true, pageName: "" };
    } else if (activeItem === "library") {
      return { showSearch: false, showUserProfile: true, showArrows: true, pageName: "My Library" };
    } else if (activeItem === "settings") {
      return { showSearch: false, showUserProfile: true, showArrows: true, pageName: "Account" };
    } else if (activeItem === "help") {
      return { showSearch: true, showUserProfile: true, showArrows: true, pageName: "" };
    } else {
      return { showSearch: false, showUserProfile: true, showArrows: true, pageName: "Search Results" };
    }
  };

  const { showSearch, showUserProfile, showArrows, pageName } = getHeaderVisibility();

  return (
    <main className="main-content">
      <div className="sidebar_container">
        <Sidebar
          activeItem={activeItem}
          setActiveItem={handleActiveItemChange}
          resetSearch={resetSearch}
        />
      </div>

      <div className="bookInfo_container">
        <Header
          showSearch={showSearch}
          showUserProfile={showUserProfile}
          showArrows={showArrows}
          pageName={pageName}
          searchQuery={searchQuery}
          onSearch={(query) => setSearchQuery(query)}
          onProfileClick={handleProfileClick}
        />

        <div className="bookInfo_body">
          {loading ? (
            <div className="loading-spinner">
              <img src={loadingSpinner} alt="Loading..." />
            </div>
          ) : (
            <div className="carousel-container" role="region" aria-label="Book carousel">
              <button
                className="carousel-arrow-button"
                type="button"
                onClick={handlePrev}
                aria-label="Previous book"
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handlePrev(); } }}
                onFocus={() => speakText("Previous book")}
              >
                <div className="left-arrow">
                  <img src={bigLeftArrow} alt="left arrow" />
                </div>
              </button>

              <div className="carousel-content" aria-live="polite">
                <img className="bookinfo_cover" src={currentBook.thumbnail} alt={currentBook.title || "Book Cover"} />
                <div className="book_info">
                  <div className="bookinfo_title_play">
                    <div className="bookinfo_title">{currentBook.title}</div>

                    <button
                      className="bookinfo_play"
                      type="button"
                      onClick={(e) => handlePlayClick(e, currentBook._id)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handlePlayClick(e, currentBook._id); } }}
                      aria-label={`Play ${currentBook.title}`}
                      onFocus={() => speakText(`Play ${currentBook.title}`)}
                    >
                      <img src={playIcon} alt="play icon" />
                    </button>
                  </div>

                  <div className="bookinfo_author_save">
                    <div className="bookinfo_author">{(currentBook.author_list || []).join(", ")}</div>
                    <button
                      className="bookinfo_save"
                      type="button"
                      onClick={() => toggleBookmark(currentBook._id)}
                      aria-pressed={isBookmarked}
                      aria-label={isBookmarked ? "Remove from Read Later" : "Add to Read Later"}
                      onFocus={() => speakText(isBookmarked ? "Remove from Read Later" : "Add to Read Later")}
                    > 
                      <img
                        src={isBookmarked ? tickReadLaterIcon : readlaterIcon}
                        alt={isBookmarked ? "Bookmarked" : "Bookmark"}
                      />
                      <span className="save-text">Add to Read Later</span>
                    </button>
                  </div>
                  <p className="bookinfo_description">{currentBook.description}</p>
                </div>
              </div>

              <button
                className="carousel-arrow-button"
                type="button"
                onClick={handleNext}
                aria-label="Next book"
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleNext(); } }}
                onFocus={() => speakText("Next book")}
              >
                <div className="right-arrow">
                  <img src={bigRightArrow} alt="right arrow" />
                </div>
              </button>
            </div>
          )}
        </div>
        {/* visually hidden live region for additional announcements if needed */}
        <div className="sr-only" aria-live="polite" aria-atomic="true" id="bookinfo-live" />
      </div>
    </main>
  );
};

export default BookInfo;
